/* Custom Input Component to be used with Formik */
import React, { useMemo, useEffect, useRef } from 'react';
import styled from 'styled-components/macro';
import PropTypes from 'prop-types';
import {
  Text,
  FormErrorMessage,
  FormFieldInfo,
  DatePicker,
  Icon,
} from 'components/atoms';
import { modifiersType } from 'types'
import { applyStyleModifiers } from 'styled-components-modifiers';
import { inputStyle } from 'components/atoms/Form/Field'
import { useOnClickOutside } from 'hooks'
import { useKeyPressEvent } from 'react-use';
import { isValid as isValidDate, format } from 'date-fns'

// this spacing is used below the error message, this makes the margin feel more natural
// when the error happears and disappears
const InputSpacingFix = styled.div`
  height: ${props => props.theme.spacing.m};
  width: 100%;
`;

const FIELD_CUSTOM_MODIFIERS = {
  stretch: () => `
   width: 100%;
  `,
};


const FieldDate = ({
  className,
  title,
  field, // { name, value, onChange, onBlur }
  form: {
    touched, errors, isValid, isSubmitting, setFieldTouched, setFieldValue
  }, // also values, setXXXX, handleXXXX, dirty, isValid, status, etc.
  info,
  shouldFocus,
  isDisabled,
  ...props
}) => {
  const [isDatePickerVisible, setDatePickerVisible] = React.useState(false)

  // focus on error after submit
  const fieldRef = useRef(null);
  const wrapperRef = useRef(null);
  useEffect(() => {
    if (isValid || !isSubmitting) return;
    const firstErrorKey = Object.keys(errors)[0];

    if (!isValid && fieldRef && fieldRef.current && firstErrorKey === field.name) {
      fieldRef.current.focus();
      setFieldTouched(field.name, true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitting, isValid, errors]);

  // focus on mount
  useEffect(() => {
    if (shouldFocus && fieldRef && fieldRef.current) {
      fieldRef.current.focus();
    }
  }, [shouldFocus, fieldRef])

  const isError = useMemo(() => touched[field.name] && errors[field.name], [
    touched,
    errors,
    field.name,
  ]);

  const handleBlur = () => {
    if ('onBlur' in field && typeof field.onBlur === 'function') field.onBlur(field.name)
    if ('onBlur' in props && typeof props.onBlur === 'function') props.onBlur()
    setFieldTouched(field.name, true)
  }

  const handleFocus = () => {
    if ('onFocus' in field && typeof field.onFocus === 'function') field.onFocus(field.name)
    if ('onFocus' in props && typeof props.onFocus === 'function') props.onFocus()
    setDatePickerVisible(true)
  }

  const handleChangeDatePicker = (date) => {
    if (isValidDate(date)) {
      setFieldValue(field.name, date.toISOString());
      setDatePickerVisible(false)
    }
  }

  const handleChangeInput = (dateString) => {
    const date = new Date(dateString)
    if (isValidDate(date)) {
      setFieldValue(field.name, date.toISOString());
      setDatePickerVisible(false)
    }
  }

  useOnClickOutside(wrapperRef, () => setDatePickerVisible(false));
  useKeyPressEvent('Enter', () => {
    if (isDatePickerVisible) setDatePickerVisible(false)
  });

  const readableDate = useMemo(() => {
    const parsedDate = field.value ? new Date(field.value) : new Date()
    const isValid = isValidDate(parsedDate)
    if (!isValid) return 'Data inválida'
    const computeDate = format(parsedDate, 'dd/MM/yyyy')
    return computeDate
  }, [field.value])

  // eslint-disable-next-line no-unused-vars
  const { onChange, ...restField } = field


  // set initial date as today
  useEffect(() => {
    if (!field.value) setFieldValue(field.name, new Date().toISOString());
  }, [field.value])

  return (
    <Wrapper className={className} isDisabled={isDisabled}>
      {title
      && (
        <Top>
          <Text modifiers={['base']}>{title}</Text>
        </Top>
      )
      }
      <FieldWrapper ref={wrapperRef}>
        <input
          type="text"
          value={readableDate}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
        <input
          style={{ display: 'none' }}
          type="text"
          onChange={handleChangeInput}
          {...restField}
          {...props}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
        <FieldIcon>
          <Icon icon="fal fa-calendar-alt" height={16} />
        </FieldIcon>
        {isDatePickerVisible && (
          <DatePickerWrapper>
            <DatePicker
              onChange={handleChangeDatePicker}
              value={new Date(field.value)}
            />
          </DatePickerWrapper>
        )}
      </FieldWrapper>
      {isError && <FormErrorMessage>{errors[field.name]}</FormErrorMessage>}
      {info && <FormFieldInfo>{info}</FormFieldInfo>}
      <InputSpacingFix />
    </Wrapper>
  );
};

FieldDate.propTypes = {
  className: PropTypes.string,
  title: PropTypes.string,
  field: PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.any,
    onBlur: PropTypes.func
  }).isRequired,
  form: PropTypes.shape({
    touched: PropTypes.shape({}),
    errors: PropTypes.shape({}),
    isSubmitting: PropTypes.bool,
    isValid: PropTypes.bool,
    setFieldTouched: PropTypes.func,
  }).isRequired,
  info: PropTypes.string,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  modifiers: modifiersType,
  shouldFocus: PropTypes.bool,
  isDisabled: PropTypes.bool,
};

FieldDate.defaultProps = {
  className: '',
  title: null,
  info: null,
  onBlur: () => { },
  onFocus: () => { },
  modifiers: [],
  shouldFocus: false,
  isDisabled: false,
};

const Wrapper = styled.div`
  pointer-events: ${props => (props.isDisabled ? 'none' : 'initial')};
  opacity: ${props => (props.isDisabled ? 0.5 : 1)};
`

const Top = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
`;

const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  cursor: pointer;
  position: relative;
`

const DatePickerWrapper = styled.div`
  position: relative;
`

const FieldIcon = styled.div`
  position: absolute;
  top: 50%;
  left: 100%;
  transform: translate(-100%, -50%);
`

export default styled(FieldDate)`
  ${Text} {
    padding-bottom: ${props => props.theme.spacing.s};
  }
  input {
    ${inputStyle}
  }
  ${applyStyleModifiers(FIELD_CUSTOM_MODIFIERS)};
`;
